// Pure timing helpers for the browser SSE client (hooks/useSSE.ts).
//
// Extracted so the reconnect-backoff and stall-detection logic is unit-
// tested without a DOM / EventSource. No IO, no globals — just math over
// (attempt, now, lastActivity) → decisions the hook then acts on.
//
// Why a stall detector exists at all: EventSource only surfaces a dead
// stream via `onerror`, but the common real-world death modes — a proxy/LB
// idle-drop, laptop sleep/wake, a backgrounded tab, a half-open TCP socket
// — leave the connection silently wedged (open, but no bytes) and never
// fire `onerror`. The server pings every PING ms; if the client sees
// nothing for STALL ms it treats the socket as wedged and forces a
// reconnect, instead of sitting "connected" forever until a manual refresh.

/** Server SSE keepalive cadence (must match the route's KEEPALIVE_MS). */
export const SSE_PING_INTERVAL_MS = 15_000;

/**
 * Silence past this long on an OPEN socket = wedged → force reconnect.
 * ~2.7× the ping interval: long enough that a single dropped/late ping
 * doesn't cause a needless reconnect, short enough to recover quickly.
 */
export const SSE_STALL_MS = 40_000;

/** How often the client watchdog samples liveness. */
export const SSE_WATCHDOG_INTERVAL_MS = 5_000;

/** Full-jitter exponential backoff base + cap for reconnect attempts. */
export const SSE_RECONNECT_BASE_MS = 1_000;
export const SSE_RECONNECT_CAP_MS = 30_000;

/**
 * Full-jitter exponential backoff: delay = rand · min(cap, base·2^attempt).
 * `rand` is the caller's random draw in [0,1) (injected so it's testable).
 * Spreads correlated drops (a droplet dying, a deploy closing every stream)
 * so the fleet doesn't reconnect in a synchronized stampede.
 */
export function reconnectDelayMs(
  attempt: number,
  rand: number,
  baseMs: number = SSE_RECONNECT_BASE_MS,
  capMs: number = SSE_RECONNECT_CAP_MS,
): number {
  const ceiling = Math.min(capMs, baseMs * 2 ** attempt);
  return rand * ceiling;
}

/**
 * Is an OPEN socket stalled? True when total silence (no message OR ping)
 * has reached the threshold. Callers must only apply this to a socket they
 * believe is open — a closed/reconnecting stream isn't "stalled", it's
 * already being retried by the backoff path.
 */
export function isStreamStalled(
  lastActivityAt: number,
  now: number,
  stallMs: number = SSE_STALL_MS,
): boolean {
  return now - lastActivityAt >= stallMs;
}
